import React, { createContext, useContext, useState } from 'react';
import { useWeddingWebsiteQuery, WeddingWebsiteQuery, WeddingWebsiteQueryDocument } from '../generated/graphql';
import { useUpdateWeddingWebsiteMutation } from '../generated/graphql';
import { DataProxy } from 'apollo-cache';
import { useClient } from '../apollo/ApolloClientsProvider';

export const updateLayoutSuccessCallback = (cache: DataProxy, newLayout: string) => {
  const { weddingWebsite, ...rest } = cache.readQuery<WeddingWebsiteQuery>({ query: WeddingWebsiteQueryDocument })!;
  const updatedWeddingWebsite = { ...weddingWebsite, layout: newLayout };
  cache.writeQuery({
    query: WeddingWebsiteQueryDocument,
    data: { weddingWebsite: updatedWeddingWebsite, ...rest },
  });
};

export interface IWebsiteLayoutContext {
  currentLayout: string;
  updateWebsiteLayout: (layout: string) => Promise<void>;
  previewLayout?: string;
  setPreviewLayout: (layout: string) => void;
  layout: string;
  setPreviewLayoutToCurrent: () => Promise<void>;
  resetPreviewLayout: () => void;
  isUpdateLayoutLoading: boolean;
  hasLayoutChanged: boolean;
}

const WebsiteLayoutContext = createContext<IWebsiteLayoutContext | null>(null);

interface WebsiteLayoutProviderProps {
  children: React.ReactNode;
}

export const WebsiteLayoutProvider: React.FC<WebsiteLayoutProviderProps> = ({ children }) => {
  const { data } = useWeddingWebsiteQuery();
  const client = useClient();
  const currentLayout = data?.weddingWebsite?.layout || 'multi-page';

  const [updateLayout, { loading: isUpdateLayoutLoading }] = useUpdateWeddingWebsiteMutation();

  const [previewLayout, setPreviewLayout] = useState<string | undefined>(undefined);
  const [hasLayoutChanged, setHasLayoutChanged] = useState(false);

  const getComputedLayout = () => {
    return previewLayout ? previewLayout : currentLayout;
  };

  const updateWebsiteLayout = async (layout: string) => {
    await updateLayout({
      variables: {
        attributes: {
          layout,
        },
      },
      update() {
        const cache = client.cache;
        updateLayoutSuccessCallback(cache, layout);
      },
    });
    setPreviewLayout(undefined);
    setHasLayoutChanged(true);
  };

  const setPreviewLayoutToCurrent = async () => {
    if (previewLayout) {
      await updateWebsiteLayout(previewLayout);
    }
  };

  const resetPreviewLayout = () => {
    setPreviewLayout(undefined);
  };

  return (
    <WebsiteLayoutContext.Provider
      value={{
        currentLayout,
        updateWebsiteLayout,
        previewLayout,
        setPreviewLayout,
        layout: getComputedLayout(),
        setPreviewLayoutToCurrent,
        resetPreviewLayout,
        isUpdateLayoutLoading,
        hasLayoutChanged,
      }}
    >
      {children}
    </WebsiteLayoutContext.Provider>
  );
};

export const useWebsiteLayoutContext = () => {
  const context = useContext(WebsiteLayoutContext);

  if (!context) {
    throw new Error('useWebsiteLayoutContext must be used within WebsiteLayoutProvider');
  }

  return context;
};
